import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { UserRole } from "modules/auth/types";

export type PrivatePage =
  | "dashboard"
  | "issuerAssets"
  | "issuerAsset"
  | "issuerProposals"
  | "issuerProposal"
  | "issuerProposalNew"
  | "debtorAssets"
  | "debtorAsset"
  | "settings";

export interface PrivateRouteDefinition {
  path: string;
  title: string;
  description: string;
  component: LazyExoticComponent<ComponentType>;
  allowedRoles?: readonly UserRole[];
}

export const privateRoutes = {
  dashboard: {
    path: "/portal-cliente",
    title: "Portal do Cliente | OXY DTVM",
    description:
      "Ambiente para acompanhamento operacional, documentos e relacionamento com a OXY DTVM.",
    allowedRoles: ["emissor", "devedor"],
    component: lazy(() =>
      import("pages/client/dashboard").then((module) => ({
        default: module.ClientDashboardPage,
      })),
    ),
  },
  issuerAssets: {
    path: "/portal/emissor/ativos",
    title: "Operações do Emissor | OXY DTVM",
    description:
      "Visao consolidada das operacoes vinculadas ao emissor, com acesso rapido ao acompanhamento operacional.",
    allowedRoles: ["emissor"],
    component: lazy(() =>
      import("pages/client/issuer-assets").then((module) => ({
        default: module.IssuerAssetsPage,
      })),
    ),
  },
  issuerAsset: {
    path: "/portal/emissor/ativo/:id",
    title: "Detalhe da Operação do Emissor | OXY DTVM",
    description:
      "Acompanhamento da operacao selecionada do emissor, com obrigacoes, documentos e atualizacoes operacionais.",
    allowedRoles: ["emissor"],
    component: lazy(() =>
      import("pages/client/issuer-asset").then((module) => ({
        default: module.IssuerAssetPage,
      })),
    ),
  },
  issuerProposals: {
    path: "/portal/emissor/propostas",
    title: "Propostas do Emissor | OXY DTVM",
    description:
      "Fluxo de propostas enviadas pelo emissor com acompanhamento do retorno operacional.",
    allowedRoles: ["emissor"],
    component: lazy(() =>
      import("pages/client/issuer-proposals").then((module) => ({
        default: module.IssuerProposalsPage,
      })),
    ),
  },
  issuerProposalNew: {
    path: "/portal/emissor/propostas/nova",
    title: "Nova Proposta do Emissor | OXY DTVM",
    description:
      "Solicitacao de nova proposta pelo portal do emissor.",
    allowedRoles: ["emissor"],
    component: lazy(() =>
      import("pages/client/issuer-proposal-new").then((module) => ({
        default: module.IssuerProposalNewPage,
      })),
    ),
  },
  issuerProposal: {
    path: "/portal/emissor/propostas/:id",
    title: "Detalhe da Proposta do Emissor | OXY DTVM",
    description:
      "Acompanhamento da proposta enviada pelo emissor e validacao do retorno.",
    allowedRoles: ["emissor"],
    component: lazy(() =>
      import("pages/client/issuer-proposal").then((module) => ({
        default: module.IssuerProposalPage,
      })),
    ),
  },
  debtorAssets: {
    path: "/portal/devedor/emissoes",
    title: "Emissões do Devedor | OXY DTVM",
    description:
      "Visao consolidada das emissoes em que o usuario autenticado consta como devedor.",
    allowedRoles: ["devedor"],
    component: lazy(() =>
      import("pages/client/debtor-assets").then((module) => ({
        default: module.DebtorAssetsPage,
      })),
    ),
  },
  debtorAsset: {
    path: "/portal/devedor/emissao/:id",
    title: "Detalhe da Emissão do Devedor | OXY DTVM",
    description:
      "Acompanhamento da emissao selecionada pelo devedor, com obrigacoes, documentos e atualizacoes operacionais.",
    allowedRoles: ["devedor"],
    component: lazy(() =>
      import("pages/client/debtor-asset").then((module) => ({
        default: module.DebtorAssetPage,
      })),
    ),
  },
  settings: {
    path: "/portal/configuracoes",
    title: "Configurações | OXY DTVM",
    description:
      "Area de configuracoes da conta do usuario autenticado, com verificacao de credenciais.",
    component: lazy(() =>
      import("pages/client/settings").then((module) => ({
        default: module.SettingsPage,
      })),
    ),
  },
} as const satisfies Record<PrivatePage, PrivateRouteDefinition>;
